import { Component, DestroyRef, type OnChanges, type OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { CptGoogleTagmanagerService, type GTMEventData, MatomoIntegrationService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { type HttpErrorResponse } from '@angular/common/http'
import { catchError, filter, tap } from 'rxjs/operators'
import { EMPTY } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NGXLogger } from 'ngx-logger'
import { DashEcoService } from '../../../../services/dash-eco.service'
import { DataService } from '../../../../services/data.service'
import { LanguageService } from '../../../../services/language.service'
import { LocalStorageService } from '../../../../services/local-storage.service'
import {
  type InterestDTO, PersonalizationService, type PersonalizeDTO
} from '../../../../services/personalization.service'
import { SharedService } from '../../../../services/shared.service'
import { type BrandDTO } from '../../../../services/vehicles.service'
import { isEqual } from 'lodash'

import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxGlideModule } from 'ngx-glide'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
@Component({
  selector: 'customer-portal-app-revolution-user-personalisation',
  standalone: true,
  templateUrl: './user-personalisation.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule
  ]
})

export class RevolutionUserPersonalisationComponent implements OnInit, OnChanges {
  primaryLang = ''
  brands: BrandDTO[] = []
  selectedBrands: BrandDTO[] = []
  selectedConsents: InterestDTO[] = []
  error: HttpErrorResponse | null = null
  getError: HttpErrorResponse | null = null
  consents: InterestDTO[] = []
  bodyTypes: string[] = []
  energyEfficiencies: string[] = []
  equipmentLevels: string[] = []
  fuelTypes: string[] = []
  powertrains: string[] = []
  transmissions: string[] = []
  vehicleUsages: string[] = []
  vehicleColor: string[] = []

  selectedBodyTypes: string[] = []
  selectedEnergyEfficiencies: string[] = []
  selectedEquipmentLevels = ''
  selectedFuelTypes: string[] = []
  selectedPowertrains: string[] = []
  selectedTransmissions: string[] = []
  selectedVehicleUsages: string[] = []
  selectedVehicleColor: string[] = []
  personalizationValues!: PersonalizeDTO
  personalizationValuesBackup!: PersonalizeDTO
  loading = false
  country: string
  showDashEcoNews
  saveIsDisabled = true

  bodyTypeIcons: Record<string, string> = {
    suv: 'SUV',
    convertible: 'Cabrio',
    coupe: 'Coupe',
    compact: 'Kleinwagen',
    station_wagon: 'Kombi',
    van: 'Van2',
    sedan: 'Limousine',
    pickup: 'Pickup',
    camp: 'RV'
  }

  constructor (
    private readonly personalizationService: PersonalizationService,
    private readonly logger: NGXLogger,
    public readonly dialog: MatDialog,
    private readonly dataService: DataService,
    private readonly languageService: LanguageService,
    private readonly cptGtmService: CptGoogleTagmanagerService,
    public readonly sharedService: SharedService,
    public readonly dashEcoService: DashEcoService,
    private readonly localStorageService: LocalStorageService,
    private readonly matomoIntegrationService: MatomoIntegrationService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>,
    private readonly destroyRef: DestroyRef
  ) {
    this.dataService.onLanguageChange.pipe(
      filter(language => this.languageService.checkIfExists(language)),
      tap(language => (this.primaryLang = language.toLowerCase())),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe()
    this.country = this.remoteConfigService.get('country.code')
    this.showDashEcoNews = this.remoteConfigService.get('showDashEcoNews')
  }

  ngOnChanges (): void {
    this.loading = false
  }

  ngOnInit (): void {
    this.brands = []
    this.selectedBrands = []
    this.selectedConsents = []
    this.selectedTransmissions = []
    this.selectedBodyTypes = []
    this.selectedFuelTypes = []
    this.selectedPowertrains = []
    this.selectedVehicleUsages = []
    this.selectedVehicleColor = []
    this.selectedEnergyEfficiencies = []
    this.getPersonalizationEnums()
    this.getPersonalizationValues()
  }

  getPersonalizationEnums (): void {
    this.personalizationService
      .getPersonalizationEnums()
      .pipe(
        tap(value => {
          this.loading = false
          this.consents = value?.interests ?? []
          this.brands = value?.brands ?? []
          this.bodyTypes = value?.bodyTypes ?? []
          this.energyEfficiencies = value?.energyEfficiencies ?? []
          this.equipmentLevels = value?.equipmentLevels ?? []
          this.fuelTypes = value?.fuelTypes ?? []
          this.powertrains = value?.powertrains ?? []
          this.transmissions = value?.transmissions ?? []
          this.vehicleUsages = value?.vehicleUsages ?? []
          this.vehicleColor = value?.vehicleColor ?? []
          // set family first in array
          this.vehicleUsages.sort(function (a, b) {
            if (a === 'family') {
              return -1
            }
            if (b === 'family') {
              return 1
            }
            return 0
          })
        }),
        catchError((error: HttpErrorResponse) => {
          this.getError = error
          this.logger.error(error)
          return EMPTY
        })
      ).subscribe()
  }

  getPersonalizationValues (): void {
    this.personalizationService
      .getPersonalizationValues()
      .pipe(
        tap(value => {
          this.personalizationValues = value
          this.personalizationValuesBackup = structuredClone(value)
          this.selectedConsents = value?.interests ?? []
          this.selectedBodyTypes = value?.bodyTypes ?? []
          this.selectedEquipmentLevels = value?.equipmentLevel ?? ''
          this.selectedEnergyEfficiencies = value?.energyEfficiencies ?? []
          this.selectedFuelTypes = value?.fuelTypes ?? []
          this.selectedTransmissions = value?.transmissions ?? []
          this.selectedPowertrains = value?.powertrains ?? []
          this.selectedVehicleUsages = value?.vehicleUsages ?? []
          this.selectedVehicleColor = value?.vehicleColor ?? []
          this.selectedBrands = value?.brands ?? []
        }),
        catchError((error: HttpErrorResponse) => {
          this.logger.error(error)
          return EMPTY
        })
      ).subscribe()
  }

  selectBrand (selectedBrand: BrandDTO): void {
    if (this.selectedBrands?.find(s => s.name === selectedBrand.name) != null) {
      this.selectedBrands = this.selectedBrands.filter(
        item => item.name !== selectedBrand.name
      )
    } else if (this.selectedBrands != null) {
      this.selectedBrands.push(selectedBrand)
    }

    this.checkEqualData()
  }

  checkBrand (selectedBrand: BrandDTO): boolean {
    return !!this.selectedBrands?.some(b => b.name === selectedBrand.name)
  }

  selectConsent (selectedConsent: InterestDTO): void {
    if (
      this.selectedConsents?.find(s => s.code === selectedConsent.code) != null
    ) {
      this.selectedConsents = this.selectedConsents.filter(
        item => item.code !== selectedConsent.code
      )
    } else if (this.selectedConsents != null) {
      this.selectedConsents.push(selectedConsent)
    }

    this.checkEqualData()
  }

  checkConsent (selectedConsent: InterestDTO): boolean {
    return !!this.selectedConsents?.some(s => s.code === selectedConsent.code)
  }

  selectTransmission (transmission: string): void {
    if (this.selectedTransmissions?.find(s => s === transmission) != null) {
      this.selectedTransmissions = this.selectedTransmissions.filter(
        item => item !== transmission
      )
    } else if (this.selectedTransmissions != null) {
      this.selectedTransmissions.push(transmission)
    }

    this.checkEqualData()
  }

  checkTransmission (transmission: string): boolean {
    return !!this.selectedTransmissions?.includes(transmission)
  }

  selectVehicleUsage (vehicleUsage: string): void {
    if (this.selectedVehicleUsages?.find(s => s === vehicleUsage) != null) {
      this.selectedVehicleUsages = this.selectedVehicleUsages.filter(
        item => item !== vehicleUsage
      )
    } else if (this.selectedVehicleUsages != null) {
      this.selectedVehicleUsages.push(vehicleUsage)
    }
    this.checkEqualData()
  }

  checkVehicleUsage (vehicleUsage: string): boolean {
    return !!this.selectedVehicleUsages?.includes(vehicleUsage)
  }

  selectEquipmentLevels (equipmentLevels: string): void {
    this.selectedEquipmentLevels = equipmentLevels

    this.checkEqualData()
  }

  checkEquipmentLevels (equipmentLevels: string): boolean {
    return this.selectedEquipmentLevels === equipmentLevels
  }

  selectEnergyEfficiency (energyEfficiency: string): void {
    if (
      this.selectedEnergyEfficiencies?.find(s => s === energyEfficiency) != null
    ) {
      this.selectedEnergyEfficiencies = this.selectedEnergyEfficiencies.filter(
        item => item !== energyEfficiency
      )
    } else if (this.selectedEnergyEfficiencies != null) {
      this.selectedEnergyEfficiencies.push(energyEfficiency)
    }

    this.checkEqualData()
  }

  checkEnergyEfficiency (energyEfficiency: string): boolean {
    return !!this.selectedEnergyEfficiencies?.includes(energyEfficiency)
  }

  selectFuelType (fuelType: string): void {
    if (
      this.selectedFuelTypes != null &&
      this.selectedFuelTypes.length > 0 &&
      this.selectedFuelTypes.find(s => s === fuelType) != null
    ) {
      this.selectedFuelTypes = this.selectedFuelTypes.filter(
        item => item !== fuelType
      )
    } else if (this.selectedFuelTypes != null) {
      this.selectedFuelTypes.push(fuelType)
    }

    this.checkEqualData()
  }

  checkFuelType (fuelType: string): boolean {
    return !!this.selectedFuelTypes.includes(fuelType)
  }

  selectBodyType (bodyType: string): void {
    if (this.selectedBodyTypes?.find(s => s === bodyType) != null) {
      this.selectedBodyTypes = this.selectedBodyTypes.filter(
        item => item !== bodyType
      )
    } else if (this.selectedBodyTypes != null) {
      this.selectedBodyTypes.push(bodyType)
    }

    this.checkEqualData()
  }

  checkBodyType (bodyType: string): boolean {
    return !!this.selectedBodyTypes?.includes(bodyType)
  }

  selectPowertrains (powertrain: string): void {
    if (this.selectedPowertrains?.find(s => s === powertrain) != null) {
      this.selectedPowertrains = this.selectedPowertrains.filter(
        item => item !== powertrain
      )
    } else if (this.selectedPowertrains != null) {
      this.selectedPowertrains.push(powertrain)
    }

    this.checkEqualData()
  }

  checkPowertrains (powertrain: string): boolean {
    return !!this.selectedPowertrains?.includes(powertrain)
  }

  selectVehicleColor (vehicleColor: string): void {
    if (this.selectedVehicleColor?.find(s => s === vehicleColor) != null) {
      this.selectedVehicleColor = this.selectedVehicleColor.filter(
        item => item !== vehicleColor
      )
    } else if (this.selectedVehicleColor != null) {
      this.selectedVehicleColor.push(vehicleColor)
    }

    this.checkEqualData()
  }

  checkVehicleColor (vehicleColor: string): boolean {
    return !!this.selectedVehicleColor?.includes(vehicleColor)
  }

  sendGtmData (): void {
    const gtmData: GTMEventData = {
      event: 'gaEvent',
      event_name: 'personalization',
      personalization: {
        event_action: 'Personalization settings change',
        event_category: 'Personalization',
        event_label: 'Personalization',
        personalization_body_variant: this.selectedBodyTypes,
        personalization_car_usage: this.selectedVehicleUsages,
        personalization_drive_type: this.selectedPowertrains,
        personalization_equipment_level: this.selectedEquipmentLevels,
        personalization_favorite_makes: this.selectedBrands,
        personalization_fuel_type: this.selectedFuelTypes,
        personalization_transmission_type: this.selectedTransmissions,
        personalization_efficiency: this.selectedEnergyEfficiencies
      }
    }
    this.cptGtmService.send(gtmData)
  }

  savePersonalization (): void {
    this.matomoIntegrationService.trackEvent(
      'My account',
      'Personalization settings change'
    )

    this.sendGtmData()
    this.loading = true
    this.personalizationValues.interests = this.selectedConsents
    this.personalizationValues.brands = this.selectedBrands
    this.personalizationValues.powertrains = this.selectedPowertrains
    this.personalizationValues.vehicleUsages = this.selectedVehicleUsages
    this.personalizationValues.vehicleColor = this.selectedVehicleColor
    this.personalizationValues.transmissions = this.selectedTransmissions
    this.personalizationValues.fuelTypes = this.selectedFuelTypes
    this.personalizationValues.equipmentLevel = this.selectedEquipmentLevels
    this.personalizationValues.energyEfficiencies = this.selectedEnergyEfficiencies
    this.personalizationValues.bodyTypes = this.selectedBodyTypes

    this.personalizationService
      .putPersonalization(this.personalizationValues)
      .pipe(
        tap(value => {
          this.personalizationValuesBackup = structuredClone(value)
          this.saveIsDisabled = true
          this.loading = false
          this.selectedConsents = value?.interests ?? []
          this.selectedBrands = value?.brands ?? []
          this.selectedPowertrains = value?.powertrains ?? []
          this.selectedBodyTypes = value?.bodyTypes ?? []
          this.selectedEnergyEfficiencies = value?.energyEfficiencies ?? []
          this.selectedEquipmentLevels = value?.equipmentLevel ?? ''
          this.selectedFuelTypes = value?.fuelTypes ?? []
          this.selectedTransmissions = value?.transmissions ?? []
          this.selectedVehicleUsages = value?.vehicleUsages ?? []
          this.selectedVehicleColor = value?.vehicleColor ?? []
          this.sharedService.openConfirmationPopup(
            'shared.success',
            'customerPortal.customer-portal.details.change.personalisation-changed'
          )
          this.dataService.personalizationLoaded(value)
        }),
        catchError((error: HttpErrorResponse) => {
          this.sharedService.openConfirmationPopup(
            'shared.error',
            'shared.general-error-message'
          )
          this.loading = false
          this.logger.error(error)
          this.error = error
          window.scroll(0, 0)
          return EMPTY
        })
      ).subscribe()
  }

  checkEqualData (): boolean {
    const backup = this.personalizationValuesBackup
    if (!backup) return true

    const fields: Array<[keyof typeof backup, unknown, string]> = [
      ['interests', this.selectedConsents, 'interests'],
      ['brands', this.selectedBrands, 'brands'],
      ['powertrains', this.selectedPowertrains, 'powertrains'],
      ['vehicleUsages', this.selectedVehicleUsages, 'vehicleUsages'],
      ['vehicleColor', this.selectedVehicleColor, 'vehicleColor'],
      ['transmissions', this.selectedTransmissions, 'transmissions'],
      ['fuelTypes', this.selectedFuelTypes, 'fuelTypes'],
      ['equipmentLevel', this.selectedEquipmentLevels, 'equipmentLevel'],
      ['energyEfficiencies', this.selectedEnergyEfficiencies, 'energyEfficiencies'],
      ['bodyTypes', this.selectedBodyTypes, 'bodyTypes']
    ]

    for (const [key, selected, label] of fields) {
      const current = selected ?? (Array.isArray(backup[key]) ? [] : '')
      const previous = backup[key] ?? (Array.isArray(current) ? [] : '')

      const isDifferent = Array.isArray(current)
        ? !isEqual(current, previous)
        : current !== previous

      if (isDifferent) {
        this.logger.debug('Difference found in field:', label)
        this.saveIsDisabled = false
        return false
      }
    }

    this.saveIsDisabled = true
    return true
  }

  efficiencyClassArrowColor (efficiencyClass: string): string {
    let cssClass = ''
    if (efficiencyClass === 'a+') {
      efficiencyClass = 'a-plus'
    }
    cssClass = this.country + ' ' + efficiencyClass.toLowerCase()
    return cssClass
  }

  getLang (): string {
    try {
      const lang = this.localStorageService.getLang()
      return this.sharedService.stringExists(lang) ? lang : 'de'
    } catch (error) {
      this.logger.error('Failed to get language from localStorage:', error)
      return 'de'
    }
  }
}
